import { Injectable, inject } from '@angular/core'

import type { Observable } from 'rxjs'
import { catchError, combineLatest, defer, of } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'

import { SubscriptionService } from '../../services/subscription/subscription.service'
import { ApiService } from '../api.service'
import { MagazineOptions } from '../queries'
import { MagazineDto, SelectionEssenceMagazineDto } from '../responses'

@Injectable({
  providedIn: 'root',
})
export class MagazineService {
  private readonly apiService = inject(ApiService)
  private readonly subscriptionService = inject(SubscriptionService)

  // The published magazine catalog rarely changes within a session, so it's
  // fetched once (in full, with all detail fields) and kept as in-memory
  // state shared between every consumer (home page grid, filter dropdown,
  // load-more, selection & essence) instead of each issuing its own network
  // call. refCount: false is deliberate: once the first subscriber triggers
  // the fetch, the result stays cached for the lifetime of the app even if
  // every consumer briefly unsubscribes (e.g. between page navigations) -
  // refCount: true would tear the multicast down and refetch on the next
  // subscriber, which defeats the point of "this never changes".
  private readonly allMagazines$: Observable<{
    items: MagazineDto[]
    subscribedMagazineIds: Set<number>
  }> = defer(() =>
    combineLatest({
      magazinesResult: this.apiService.getDetailedMagazines(1, -1),
      subscriptions: this.subscriptionService.getSubscriptions(),
    })
  ).pipe(
    map(({ magazinesResult, subscriptions }) => ({
      items: magazinesResult.data.Magazines.docs,
      subscribedMagazineIds: new Set(
        subscriptions.flatMap(sub => sub.product.magazine.map(mag => mag.id))
      ),
    })),
    catchError((error: unknown) => {
      console.error('Error fetching magazines:', error)
      return of({ items: [], subscribedMagazineIds: new Set<number>() })
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  )

  getMagazines(
    includeSelectionEssence = false,
    includeArchived = false
  ): Observable<{ items: MagazineDto[]; total?: number }> {
    return this.allMagazines$.pipe(
      map(({ items, subscribedMagazineIds }) => {
        const filteredItems = items.filter(mag => {
          // Exclude selectionAndEssence if not included
          if (!includeSelectionEssence && mag.selectionAndEssence) return false
          // Exclude archived if not in subscriptions, unless includeArchived is true
          if (mag.archived && !includeArchived) {
            if (subscribedMagazineIds.size === 0) return false
            return subscribedMagazineIds.has(mag.id)
          }
          return true
        })

        return { items: filteredItems, total: filteredItems.length }
      })
    )
  }

  getMagazineById(
    id: number,
    include: MagazineOptions = new MagazineOptions()
  ): Observable<MagazineDto | undefined> {
    return this.apiService.getMagazineById(id, include).pipe(
      map(result => {
        return result.data.Magazine
      })
    )
  }

  getSelectionEssenceMagazines(): Observable<{
    items: SelectionEssenceMagazineDto[]
    total?: number
  }> {
    return this.getMagazines(true).pipe(
      map(result => {
        const magazines = result.items
        const filtered = magazines.filter(
          (mag: MagazineDto) => mag.selectionAndEssence === true
        )
        return {
          items: filtered as SelectionEssenceMagazineDto[],
          total: filtered.length,
        }
      })
    )
  }
}
